"use client";

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  BusinessDetail,
  BusinessListItem,
  BusinessStats,
  DetailModalTab,
} from "@/components/BusinessListParent/types";

// Pagination interface
interface BusinessListPagination {
  count: number;
  page: number;
  pageSize: number;
}

// Detail modal state
interface DetailModalState {
  open: boolean;
  businessId: string | null;
  activeTab: DetailModalTab;
}

// Suspend modal state
interface SuspendModalState {
  open: boolean;
}

// Slice state interface
interface BusinessListState {
  // Business list
  businesses: BusinessListItem[];
  businessesLoading: boolean;
  businessesPagination: BusinessListPagination;

  // Stats
  stats: BusinessStats | null;
  statsLoading: boolean;

  // Detail modal
  detailModal: DetailModalState;
  selectedBusiness: BusinessDetail | null;
  selectedBusinessLoading: boolean;
  selectedBusinessError: string | null;

  // Edit mode
  editMode: boolean;
  editFormData: BusinessDetail | null;
  editFieldErrors: Record<string, string[]> | null;
  editSaving: boolean;

  // Suspend modal
  suspendModal: SuspendModalState;

  // Deactivate modal
  deactivateModal: { open: boolean };

  // Reactivate modal
  reactivateModal: { open: boolean };

  // Action loading (suspend/activate/deactivate/reactivate)
  actionLoading: boolean;
}

// Initial state
const initialState: BusinessListState = {
  businesses: [],
  businessesLoading: false,
  businessesPagination: {
    count: 0,
    page: 1,
    pageSize: 25,
  },

  stats: null,
  statsLoading: false,

  detailModal: {
    open: false,
    businessId: null,
    activeTab: "business",
  },
  selectedBusiness: null,
  selectedBusinessLoading: false,
  selectedBusinessError: null,

  editMode: false,
  editFormData: null,
  editFieldErrors: null,
  editSaving: false,

  suspendModal: {
    open: false,
  },

  deactivateModal: {
    open: false,
  },

  reactivateModal: {
    open: false,
  },

  actionLoading: false,
};

const businessListSlice = createSlice({
  name: "businessList",
  initialState,
  reducers: {
    // Business list
    setBusinesses: (state, action: PayloadAction<BusinessListItem[]>) => {
      state.businesses = action.payload;
    },
    setBusinessesLoading: (state, action: PayloadAction<boolean>) => {
      state.businessesLoading = action.payload;
    },
    setBusinessesPagination: (
      state,
      action: PayloadAction<BusinessListPagination>,
    ) => {
      state.businessesPagination = action.payload;
    },
    updateBusinessesPaginationPage: (state, action: PayloadAction<number>) => {
      state.businessesPagination.page = action.payload;
    },
    updateBusinessesPaginationPageSize: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.businessesPagination.pageSize = action.payload;
      state.businessesPagination.page = 1; // Reset to first page when page size changes
    },

    // Stats
    setStats: (state, action: PayloadAction<BusinessStats>) => {
      state.stats = action.payload;
    },
    setStatsLoading: (state, action: PayloadAction<boolean>) => {
      state.statsLoading = action.payload;
    },

    // Detail modal
    openDetailModal: (state, action: PayloadAction<string>) => {
      state.detailModal.open = true;
      state.detailModal.businessId = action.payload;
      state.detailModal.activeTab = "business";
      state.selectedBusinessError = null;
    },
    closeDetailModal: (state) => {
      state.detailModal.open = false;
      state.detailModal.businessId = null;
      state.detailModal.activeTab = "business";
      state.selectedBusiness = null;
      state.selectedBusinessError = null;
      // Reset edit mode when closing
      state.editMode = false;
      state.editFormData = null;
      state.editFieldErrors = null;
      state.editSaving = false;
    },
    setDetailModalTab: (state, action: PayloadAction<DetailModalTab>) => {
      state.detailModal.activeTab = action.payload;
    },
    setSelectedBusiness: (
      state,
      action: PayloadAction<BusinessDetail | null>,
    ) => {
      state.selectedBusiness = action.payload;
    },
    setSelectedBusinessLoading: (state, action: PayloadAction<boolean>) => {
      state.selectedBusinessLoading = action.payload;
    },
    setSelectedBusinessError: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.selectedBusinessError = action.payload;
    },

    // Edit mode
    enterEditMode: (state) => {
      if (state.selectedBusiness) {
        state.editMode = true;
        state.editFormData = JSON.parse(JSON.stringify(state.selectedBusiness));
        state.editFieldErrors = null;
      }
    },
    exitEditMode: (state) => {
      state.editMode = false;
      state.editFormData = null;
      state.editFieldErrors = null;
      state.editSaving = false;
    },
    updateEditBusinessField: (
      state,
      action: PayloadAction<{ field: string; value: string }>,
    ) => {
      if (!state.editFormData) return;
      const { field, value } = action.payload;
      (state.editFormData as Record<string, unknown>)[field] = value;
    },
    updateEditAddressField: (
      state,
      action: PayloadAction<{ field: string; value: string }>,
    ) => {
      if (!state.editFormData) return;
      const { field, value } = action.payload;
      (state.editFormData as Record<string, unknown>)[field] = value;
    },
    updateEditContact: (
      state,
      action: PayloadAction<{
        contactId: string;
        field: string;
        value: string;
      }>,
    ) => {
      if (!state.editFormData) return;
      const { contactId, field, value } = action.payload;
      const contact = state.editFormData.contacts.find(
        (c) => c.id === contactId,
      );
      if (!contact) return;
      (contact as Record<string, unknown>)[field] = value;
    },
    updateEditContactAddress: (
      state,
      action: PayloadAction<{
        contactId: string;
        field: string;
        value: string;
      }>,
    ) => {
      if (!state.editFormData) return;
      const { contactId, field, value } = action.payload;
      const contact = state.editFormData.contacts.find(
        (c) => c.id === contactId,
      );
      if (!contact) return;
      (contact as Record<string, unknown>)[field] = value;
    },
    addEditContact: (state) => {
      if (!state.editFormData) return;
      state.editFormData.contacts.push({
        // `new-` prefix marks a client-side draft contact so the PATCH
        // transformer omits the id and the backend creates a fresh record.
        id: `new-${crypto.randomUUID()}`,
        is_primary: false,
        full_name: "",
        role: "",
        email_address: "",
        phone_number: "",
        address_line1: "",
        address_line2: "",
        city: "",
        postcode: "",
        country: "GB",
      });
    },
    removeEditContact: (state, action: PayloadAction<string>) => {
      if (!state.editFormData) return;
      state.editFormData.contacts = state.editFormData.contacts.filter(
        (c) => c.id !== action.payload,
      );
    },
    setEditFieldErrors: (
      state,
      action: PayloadAction<Record<string, string[]> | null>,
    ) => {
      state.editFieldErrors = action.payload;
    },
    setEditSaving: (state, action: PayloadAction<boolean>) => {
      state.editSaving = action.payload;
    },

    // Suspend modal
    openSuspendModal: (state) => {
      state.suspendModal.open = true;
    },
    closeSuspendModal: (state) => {
      state.suspendModal.open = false;
    },

    // Deactivate modal
    openDeactivateModal: (state) => {
      state.deactivateModal.open = true;
    },
    closeDeactivateModal: (state) => {
      state.deactivateModal.open = false;
    },

    // Reactivate modal
    openReactivateModal: (state) => {
      state.reactivateModal.open = true;
    },
    closeReactivateModal: (state) => {
      state.reactivateModal.open = false;
    },

    // Action loading
    setActionLoading: (state, action: PayloadAction<boolean>) => {
      state.actionLoading = action.payload;
    },

    // Reset state
    resetBusinessListState: () => initialState,
  },
});

export const {
  setBusinesses,
  setBusinessesLoading,
  setBusinessesPagination,
  updateBusinessesPaginationPage,
  updateBusinessesPaginationPageSize,
  setStats,
  setStatsLoading,
  openDetailModal,
  closeDetailModal,
  setDetailModalTab,
  setSelectedBusiness,
  setSelectedBusinessLoading,
  setSelectedBusinessError,
  enterEditMode,
  exitEditMode,
  updateEditBusinessField,
  updateEditAddressField,
  updateEditContact,
  updateEditContactAddress,
  addEditContact,
  removeEditContact,
  setEditFieldErrors,
  setEditSaving,
  openSuspendModal,
  closeSuspendModal,
  openDeactivateModal,
  closeDeactivateModal,
  openReactivateModal,
  closeReactivateModal,
  setActionLoading,
  resetBusinessListState,
} = businessListSlice.actions;

export default businessListSlice.reducer;
